import { useEffect, useMemo, useRef, useState } from "react";

import { Link } from "react-router-dom";

import { useDispatch, useSelector } from "react-redux";

import {

  ArrowLeft,

  CheckCircle2,

  Clock3,

  Pencil,

  Plus,

  Search,

  Save,

  KeyRound,
  Trash2,

  UserX,

  Users,

} from "lucide-react";

import {

  Card,

  CardContent,

} from "@/shared/components/ui/card";

import {

  Button,

} from "@/shared/components/ui/button";

import {

  Input,

} from "@/shared/components/ui/input";

import {

  Label,

} from "@/shared/components/ui/label";

import {

  Badge,

} from "@/shared/components/ui/badge";

import {

  Select,

  SelectContent,

  SelectItem,

  SelectTrigger,

  SelectValue,

} from "@/shared/components/ui/select";

import {

  Table,

  TableBody,

  TableCell,

  TableHead,

  TableHeader,

  TableRow,

} from "@/shared/components/ui/table";

import {

  Dialog,

  DialogContent,

  DialogDescription,

  DialogFooter,

  DialogHeader,

  DialogTitle,

} from "@/shared/components/ui/dialog";

import {

  fetchTeamMembersThunk,

  createTeamMemberThunk,

  reactivateTeamMemberThunk,

  updateTeamMemberThunk,

  updateTeamMemberStatusThunk,

  deleteTeamMemberThunk,

} from "@/modules/team/store/teamThunk";

import {
  getJobsForTransfer,
  requestActiveTeamMemberTransfer,
  type OwnershipAssignment,
} from "@/modules/team/api/team.api";

import {
  getTeamMemberFeaturePermissions,
  setTeamMemberFeaturePermission,
} from "@/modules/team/api/teamMemberFeaturePermissionApi";
import {
  getReactivationTransferRequests,
  decideReactivationTransferRequest,
  type ReactivationTransferRequest,
} from "@/modules/team/api/team.api";

import {

  showOverlay,

  hideOverlay,

} from "@/store/loaderSlice";

import type { RootState } from "@/store/store";

import AlreadyAppliedDialog from "@/shared/components/dialogs/AlreadyAppliedDialog";

/* =========================================================

   TYPES

========================================================= */

interface TeamMember {

  id: string;

  name: string;

  email: string;

  role: string;

  post: string;

  department: string;

  location?: string;

  status: string;

  createdBy?: string;

  hrAdminId?: string | null;

  [key: string]: any;

}

interface DeletedMemberInfo {

  memberId: string;

  name: string;

  email: string;

  requestedHrAdminId?: string;

}

interface TransferJob {
  id: string;
  title?: string;
  jobTitle?: string;
  positionTitle?: string;
  status?: string;
  [key: string]: any;
}

interface TransferMember {
  id: string;
  name: string;
  email?: string;
  status?: string;
  [key: string]: any;
}


type FeaturePermissionLevel = "ALLOWED" | "NONE";

interface ModulePermission {
  featureKey: string;
  permissionLevel: FeaturePermissionLevel;
}

/* =========================================================

   CONSTANTS

========================================================= */

const MANAGED_ROLES = [

  "RECRUITER",

  "OPERATIONS",

  "MANAGER",

] as const;

const DEPARTMENTS = [

  "Human Resources",

  "Talent Acquisition",

  "Operations",

  "Administration",

  "Finance",

];

const STATUS_OPTIONS = [

  {

    value: "active",

    label: "Active",

  },

  {

    value: "on_leave",

    label: "On Leave",

  },

  {

    value: "inactive",

    label: "Inactive",

  },

];

/* =========================================================

   HELPERS

========================================================= */

const getStatusLabel = (status?: string) => {

  switch (status?.toLowerCase()) {

    case "active":

      return "Active";

    case "on_leave":

      return "On Leave";

    case "inactive":

      return "Inactive";

    default:

      return status || "-";

  }

};

const getStatusClasses = (status?: string) => {

  switch (status?.toLowerCase()) {

    case "active":

      return "border-0 bg-emerald-50 text-emerald-700 hover:bg-emerald-50";

    case "on_leave":

      return "border-0 bg-amber-50 text-amber-700 hover:bg-amber-50";

    case "inactive":

      return "border-0 bg-red-50 text-red-700 hover:bg-red-50";

    default:

      return "border-0 bg-slate-100 text-slate-600 hover:bg-slate-100";

  }

};

const getInitials = (name?: string) => {

  if (!name?.trim()) {

    return "TM";

  }

  const parts = name.trim().split(/\s+/);

  if (parts.length === 1) {

    return parts[0]

      .slice(0, 2)

      .toUpperCase();

  }

  return `${parts[0][0]}${parts[parts.length - 1][0]

    }`.toUpperCase();

};

const formatRole = (role?: string) => {
  if (!role) {
    return "-";
  }

  return role
    .toLowerCase()
    .replace(/_/g, " ")
    .replace(/\b\w/g, (character) =>
      character.toUpperCase()
    );
};

const formatFeatureName = (featureKey: string) =>
  featureKey
    .toLowerCase()
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");

/* =========================================================

   COMPONENT

========================================================= */

export default function HRMyTeamPage() {

  const dispatch = useDispatch<any>();

  const {

    members,

    loading,

    saving,

    error,

  } = useSelector(

    (state: RootState & { team: any }) =>

      state.team

  );

  /* =======================================================

     FILTERS

  ======================================================= */

  const [searchQuery, setSearchQuery] =

    useState("");

  const [filterRole, setFilterRole] =

    useState("all");

  const [filterStatus, setFilterStatus] =

    useState("all");

  /* =======================================================

     ADD / EDIT DIALOG

  ======================================================= */

  const [showDialog, setShowDialog] =

    useState(false);

  const [editingMember, setEditingMember] =

    useState<TeamMember | null>(null);

  /* =======================================================

     DELETE DIALOG

  ======================================================= */

  const [showDeletePopup, setShowDeletePopup] =

    useState(false);

  const [deleteTarget, setDeleteTarget] =

    useState<TeamMember | null>(null);

  /* SH-22: Feature permissions dialog */
  const [showPermissionsDialog, setShowPermissionsDialog] = useState(false);
  const [permissionTarget, setPermissionTarget] = useState<TeamMember | null>(null);
  const [modulePermissions, setModulePermissions] = useState<ModulePermission[]>([]);
  const [permissionsLoading, setPermissionsLoading] = useState(false);
  const [permissionSavingKey, setPermissionSavingKey] = useState<string | null>(null);
  const [permissionsError, setPermissionsError] = useState("");
  const [permissionsNotice, setPermissionsNotice] = useState("");

  /* =======================================================

     SUCCESS DIALOG

  ======================================================= */

  const [showSuccessPopup, setShowSuccessPopup] =

    useState(false);

  const [transferJobs, setTransferJobs] = useState<TransferJob[]>([]);
  const [replacementMembers, setReplacementMembers] = useState<TransferMember[]>([]);
  const [ownershipAssignments, setOwnershipAssignments] = useState<Record<string, string>>({});
  const [loadingTransferJobs, setLoadingTransferJobs] = useState(false);
  const [deletingMember, setDeletingMember] = useState(false);
  const [transferLoadError, setTransferLoadError] = useState("");

  const [successMessage, setSuccessMessage] =

    useState("");

  /* =======================================================

     MEMBER FORM

  ======================================================= */

  const [memberName, setMemberName] =

    useState("");

  const [memberEmail, setMemberEmail] =

    useState("");

  const [memberPassword, setMemberPassword] =

    useState("");

  const [memberRole, setMemberRole] =

    useState<string>(MANAGED_ROLES[0]);

  const [memberPost, setMemberPost] =

    useState("");

  const [memberDepartment, setMemberDepartment] =

    useState(DEPARTMENTS[0]);

  const [memberLocation, setMemberLocation] =

    useState("");

  const [memberStatus, setMemberStatus] =

    useState("active");

  /* =======================================================

     REACTIVATION DIALOG

  ======================================================= */


  const [showTransferApprovalDialog, setShowTransferApprovalDialog] =
    useState(false);

  const [sendingActiveTransferRequest, setSendingActiveTransferRequest] =
    useState(false);

  const [activeTransferMember, setActiveTransferMember] =
    useState<DeletedMemberInfo | null>(null);

  const [

    showReactivateDialog,

    setShowReactivateDialog,

  ] = useState(false);

  const [

    deletedMember,

    setDeletedMember,

  ] = useState<DeletedMemberInfo | null>(null);

  const [

    reactivateName,

    setReactivateName,

  ] = useState("");

  const [

    reactivateEmail,

    setReactivateEmail,

  ] = useState("");

  const [

    reactivatePassword,

    setReactivatePassword,

  ] = useState("");

  /* =======================================================

     SH-21 CROSS-HR REACTIVATION TRANSFER REQUESTS

  ======================================================= */

  const [incomingTransferRequests, setIncomingTransferRequests] =
    useState<ReactivationTransferRequest[]>([]);

  const [myTransferRequests, setMyTransferRequests] =
    useState<ReactivationTransferRequest[]>([]);

  const [transferRequestsLoading, setTransferRequestsLoading] =
    useState(false);

  const [processingTransferRequestId, setProcessingTransferRequestId] =
    useState<string | null>(null);

  // Used to detect approval/rejection changes between 15-second refreshes.
  // Initial page load establishes a baseline without showing stale notices.
  const previousTransferStatusesRef = useRef<Map<string, string>>(new Map());
  const transferStatusesInitializedRef = useRef(false);

  const refreshTransferRequests = async () => {
    try {
      setTransferRequestsLoading(true);
      const result = await getReactivationTransferRequests();
      const nextIncoming: ReactivationTransferRequest[] =
        Array.isArray(result?.incomingRequests) ? result.incomingRequests : [];
      const nextMine: ReactivationTransferRequest[] =
        Array.isArray(result?.myRequests) ? result.myRequests : [];

      const previousStatuses = previousTransferStatusesRef.current;
      if (transferStatusesInitializedRef.current) {
        const newlyDecided = nextMine.find((request) =>
          previousStatuses.get(request.id) === "PENDING" &&
          (request.status === "APPROVED" || request.status === "REJECTED")
        );
        const newlyArrived = [...nextIncoming, ...nextMine].find((request) =>
          !previousStatuses.has(request.id) && request.status === "PENDING"
        );

        if (newlyDecided?.status === "APPROVED") {
          setSuccessMessage(
            `Approved: ${newlyDecided.memberName} has been transferred to your HR team and reactivated.`
          );
          setShowSuccessPopup(true);
          // Refresh the destination team's member list automatically.
          void dispatch(fetchTeamMembersThunk({})).unwrap().catch((refreshError: any) => {
            console.error("Failed to refresh team members after approval:", refreshError);
          });
        } else if (newlyDecided?.status === "REJECTED") {
          setSuccessMessage(
            `Request rejected: ${newlyDecided.memberName} remains deleted and was not transferred.`
          );
          setShowSuccessPopup(true);
        } else if (newlyArrived) {
          setSuccessMessage(
            `A reactivation transfer request for ${newlyArrived.memberName} needs your attention.`
          );
          setShowSuccessPopup(true);
        }
      }

      const nextStatuses = new Map<string, string>();
      [...nextIncoming, ...nextMine].forEach((request) => {
        nextStatuses.set(request.id, request.status);
      });
      previousTransferStatusesRef.current = nextStatuses;
      transferStatusesInitializedRef.current = true;

      setIncomingTransferRequests(nextIncoming);
      setMyTransferRequests(nextMine);
    } catch (requestError) {
      console.error("Failed to load reactivation transfer requests:", requestError);
    } finally {
      setTransferRequestsLoading(false);
    }
  };

  useEffect(() => {
    void refreshTransferRequests();
  }, []);
  /* =======================================================

     INITIAL LOAD

  ======================================================= */

  useEffect(() => {

    dispatch(fetchTeamMembersThunk({}));

  }, [dispatch]);

  /* =======================================================

     RESET ADD / EDIT FORM

  ======================================================= */

  const resetDialog = () => {

    setMemberName("");

    setMemberEmail("");

    setMemberPassword("");

    setMemberRole(MANAGED_ROLES[0]);

    setMemberPost("");

    setMemberDepartment(DEPARTMENTS[0]);

    setMemberLocation("");

    setMemberStatus("active");

    setEditingMember(null);

  };

  /* =======================================================

     RESET REACTIVATION FORM

  ======================================================= */

  const resetReactivateDialog = () => {

    setDeletedMember(null);

    setReactivateName("");

    setReactivateEmail("");

    setReactivatePassword("");

  };

  /* =======================================================

     CREATE

  ======================================================= */

  const handleCreate = () => {

    resetDialog();

    setShowDialog(true);

  };

  /* =======================================================

     EDIT

  ======================================================= */

  const handleEdit = (

    member: TeamMember

  ) => {

    setEditingMember(member);

    setMemberName(member.name || "");

    setMemberEmail(member.email || "");

    setMemberPassword("");

    setMemberRole(

      member.role ||

      MANAGED_ROLES[0]

    );

    setMemberPost(member.post || "");

    setMemberDepartment(

      member.department ||

      DEPARTMENTS[0]

    );

    setMemberLocation(

      member.location || ""

    );

    setMemberStatus(

      member.status || "active"

    );

    setShowDialog(true);

  };

  /* =======================================================

     ERROR MESSAGE

  ======================================================= */

  const getErrorMessage = (

    errorValue: any

  ) => {

    if (!errorValue) {

      return "Something went wrong. Please try again.";

    }

    if (typeof errorValue === "string") {

      return errorValue;

    }

    return (

      errorValue?.response?.data?.message ||

      errorValue?.payload?.message ||

      errorValue?.message ||

      "Something went wrong. Please try again."

    );

  };

  /* =======================================================
     SH-22: FEATURE PERMISSIONS
  ======================================================= */

  const handleManagePermissions = async (member: TeamMember) => {
    setPermissionTarget(member);
    setShowPermissionsDialog(true);
    setModulePermissions([]);
    setPermissionsError("");
    setPermissionsNotice("");
    setPermissionsLoading(true);

    try {
      const result = await getTeamMemberFeaturePermissions(member.id);
      setModulePermissions(
        Array.isArray(result?.permissions) ? result.permissions : []
      );
    } catch (permissionError: any) {
      setPermissionsError(getErrorMessage(permissionError));
    } finally {
      setPermissionsLoading(false);
    }
  };

  const handleFeaturePermissionChange = async (
    featureKey: string,
    permissionLevel: FeaturePermissionLevel
  ) => {
    if (!permissionTarget || permissionSavingKey) {
      return;
    }

    const currentPermission = modulePermissions.find(
      (permission) => permission.featureKey === featureKey
    );

    if (!currentPermission || currentPermission.permissionLevel === permissionLevel) {
      return;
    }

    setPermissionSavingKey(featureKey);
    setPermissionsError("");
    setPermissionsNotice("");

    try {
      const result = await setTeamMemberFeaturePermission(
        permissionTarget.id,
        featureKey,
        permissionLevel
      );

      setModulePermissions((current) =>
        current.map((permission) =>
          permission.featureKey === featureKey
            ? {
              ...permission,
              permissionLevel: result?.permissionLevel ?? permissionLevel,
            }
            : permission
        )
      );

      setPermissionsNotice(
        `${formatFeatureName(featureKey)} access ${permissionLevel === "ALLOWED" ? "granted" : "revoked"}.`
      );
    } catch (permissionError: any) {
      setPermissionsError(getErrorMessage(permissionError));
    } finally {
      setPermissionSavingKey(null);
    }
  };

  /* =======================================================

     SAVE / CREATE / UPDATE

  ======================================================= */

  const handleSave = async () => {

    if (!memberName.trim()) {

      window.alert(

        "Please enter team member name."

      );

      return;

    }

    if (!memberEmail.trim()) {

      window.alert(

        "Please enter team member email."

      );

      return;

    }

    if (

      !editingMember &&

      !memberPassword.trim()

    ) {

      window.alert(

        "Please enter a password for the new team member."

      );

      return;

    }

    if (

      memberRole !== "RECRUITER" &&

      memberRole !== "OPERATIONS" &&

      memberRole !== "MANAGER"

    ) {

      window.alert(

        "Invalid team member role."

      );

      return;

    }

    const payload: Record<string, any> = {

      name: memberName.trim(),

      email: memberEmail.trim(),

      role: memberRole,

      post: memberPost.trim(),

      department:

        memberDepartment.trim(),

      location:

        memberLocation.trim(),

      status: memberStatus,

      ...(memberPassword.trim()

        ? {

          password: memberPassword,

        }

        : {}),

    };

    dispatch(showOverlay());

    try {

      if (editingMember) {

        await dispatch(

          updateTeamMemberThunk({

            memberId:

              editingMember.id,

            updates: payload,

          })

        ).unwrap();

        setSuccessMessage(

          "The team member details have been updated successfully."

        );

      } else {

        await dispatch(

          createTeamMemberThunk({

            ...payload,

            password: memberPassword,

          })

        ).unwrap();

        setSuccessMessage(

          "The new team member has been added successfully."

        );

      }

      await dispatch(
        fetchTeamMembersThunk({ force: true })
      ).unwrap();

      setShowDialog(false);

      resetDialog();

      setShowSuccessPopup(true);

    } catch (error: any) {



      console.error(

        "Failed to save team member:",

        error

      );

      /* ===================================================

         PREVIOUSLY DELETED ACCOUNT

      =================================================== */

      if (

        error?.errorCode ===

        "TEAM_MEMBER_PREVIOUSLY_DELETED" &&

        error?.data?.memberId

      ) {

        const deletedMemberData: DeletedMemberInfo =

        {

          memberId:

            error.data.memberId,

          name:

            error.data.name || "",

          email:

            error.data.email ||

            memberEmail.trim(),

          requestedHrAdminId:

            error.data.requestedHrAdminId,

        };

        setDeletedMember(

          deletedMemberData

        );

        setReactivateName(

          deletedMemberData.name

        );

        setReactivateEmail(

          deletedMemberData.email

        );

        setReactivatePassword("");

        setShowDialog(false);

        setShowReactivateDialog(true);

        return;

      }

      if (
        error?.errorCode === "TEAM_MEMBER_OWNED_BY_ANOTHER_HR" &&
        error?.data?.memberId
      ) {
        setActiveTransferMember({
          memberId: error.data.memberId,
          name: error.data.name || memberName.trim(),
          email: error.data.email || memberEmail.trim(),
          requestedHrAdminId: error.data.requestedHrAdminId,
        });

        setShowDialog(false);
        setShowTransferApprovalDialog(true);
        return;
      }

      window.alert(

        getErrorMessage(error)

      );

    } finally {

      dispatch(hideOverlay());

    }

  };

  const handleSendActiveTransferApproval = async () => {
    if (!activeTransferMember?.memberId || sendingActiveTransferRequest) {
      return;
    }

    setSendingActiveTransferRequest(true);
    dispatch(showOverlay());

    try {
      const response = await requestActiveTeamMemberTransfer(
        activeTransferMember.memberId
      );

      setShowTransferApprovalDialog(false);
      setActiveTransferMember(null);

      setSuccessMessage(
        response?.message || "Transfer approval request sent successfully."
      );
      setShowSuccessPopup(true);
    } catch (error: any) {
      console.error("Failed to send active transfer approval request:", error);
      window.alert(getErrorMessage(error));
    } finally {
      setSendingActiveTransferRequest(false);
      dispatch(hideOverlay());
    }
  };



  /* =======================================================

     REACTIVATE ACCOUNT

  ======================================================= */

  const handleReactivate = async () => {

    if (!deletedMember?.memberId) {

      return;

    }

    if (!reactivateName.trim()) {

      window.alert(

        "Please enter team member name."

      );

      return;

    }

    if (!reactivatePassword.trim()) {

      window.alert(

        "Please enter a new password."

      );

      return;

    }

    if (

      reactivatePassword.trim().length < 6

    ) {

      window.alert(

        "Password must be at least 6 characters."

      );

      return;

    }

    dispatch(showOverlay());

    try {

      const response = await dispatch(

        reactivateTeamMemberThunk({

          memberId:

            deletedMember.memberId,

          name:

            reactivateName.trim(),

          password:

            reactivatePassword,

          requestedHrAdminId:

            deletedMember.requestedHrAdminId,

        })

      ).unwrap();

      const operationResult = response?.data ?? response;

      setShowReactivateDialog(false);

      resetReactivateDialog();

      if (operationResult?.transferRequestCreated) {
        await refreshTransferRequests();
        const previousHrAdminName =
          operationResult.currentHrAdminName || "the previous HR Admin";
        setSuccessMessage(
          `This member is already associated with ${previousHrAdminName}. Your request has been sent to ${previousHrAdminName}. Once they approve it, this member will transfer to your team and reactivate automatically.`
        );
        setShowSuccessPopup(true);
        return;
      }

      await dispatch(

        fetchTeamMembersThunk({})

      ).unwrap();

      setSuccessMessage(

        "The previously deleted team member has been reactivated successfully."

      );

      setShowSuccessPopup(true);

    } catch (error: any) {

      console.error(

        "Failed to reactivate team member:",

        error

      );

      window.alert(

        getErrorMessage(error)

      );

    } finally {

      dispatch(hideOverlay());

    }

  };

  const handleReactivationTransferDecision = async (
    request: ReactivationTransferRequest,
    decision: "APPROVE" | "REJECT"
  ) => {
    setProcessingTransferRequestId(request.id);
    dispatch(showOverlay());

    try {
      const result = await decideReactivationTransferRequest(
        request.id,
        decision
      );

      await refreshTransferRequests();

      if (decision === "APPROVE") {
        await dispatch(fetchTeamMembersThunk({})).unwrap();
      }

      setSuccessMessage(
        result?.message ||
        (decision === "APPROVE"
          ? "The Team Member has been transferred and reactivated."
          : "The transfer and reactivation request was rejected.")
      );
      setShowSuccessPopup(true);
    } catch (decisionError: any) {
      console.error("Failed to decide transfer request:", decisionError);
      window.alert(getErrorMessage(decisionError));
      await refreshTransferRequests();
    } finally {
      setProcessingTransferRequestId(null);
      dispatch(hideOverlay());
    }
  };

  /* =======================================================

     STATUS CHANGE

  ======================================================= */

  const handleStatusChange = async (

    member: TeamMember,

    status: string

  ) => {

    const currentStatus =

      member.status?.toLowerCase();

    const nextStatus =

      status?.toLowerCase();

    if (

      !nextStatus ||

      currentStatus === nextStatus

    ) {

      return;

    }

    dispatch(showOverlay());

    try {

      await dispatch(

        updateTeamMemberStatusThunk({

          memberId: member.id,

          status: nextStatus,

        })

      ).unwrap();

      await dispatch(

        fetchTeamMembersThunk({})

      ).unwrap();

    } catch (error) {

      console.error(

        "Failed to update member status:",

        error

      );

      window.alert(

        getErrorMessage(error)

      );

    } finally {

      dispatch(hideOverlay());

    }

  };

  /* =======================================================

     DELETE

  ======================================================= */
  const handleDelete = async (member: TeamMember) => {
    setDeleteTarget(member);
    setTransferJobs([]);
    setReplacementMembers([]);
    setOwnershipAssignments({});
    setTransferLoadError("");
    setShowDeletePopup(true);
    setLoadingTransferJobs(true);

    try {
      const response: any = await getJobsForTransfer(member.id);
      const data = response?.data ?? response;

      const jobs: TransferJob[] = Array.isArray(data?.jobs)
        ? data.jobs
        : [];

      const candidates: TransferMember[] = Array.isArray(
        data?.replacementMembers
      )
        ? data.replacementMembers
        : [];

      setTransferJobs(jobs);
      setReplacementMembers(
        candidates.filter(
          (candidate) =>
            candidate.id !== member.id &&
            candidate.status?.toLowerCase() === "active"
        )
      );
    } catch (error: any) {
      console.error("Failed to load jobs for ownership transfer:", error);

      setTransferLoadError(getErrorMessage(error));
    } finally {
      setLoadingTransferJobs(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget || deletingMember || loadingTransferJobs) {
      return;
    }

    if (transferLoadError) {
      window.alert(
        "Transfer details could not be loaded. Please retry before deleting."
      );
      return;
    }

    if (transferJobs.length > 0) {
      if (replacementMembers.length === 0) {
        window.alert(
          "No active replacement team members are available."
        );
        return;
      }

      const missingAssignment = transferJobs.some(
        (job) => !ownershipAssignments[job.id]
      );

      if (missingAssignment) {
        window.alert(
          "Please select a replacement member for every job."
        );
        return;
      }
    }

    const assignments: OwnershipAssignment[] = transferJobs.map(
      (job) => ({
        jobId: job.id,
        assignedToId: ownershipAssignments[job.id],
      })
    );

    const target = deleteTarget;

    setDeletingMember(true);
    dispatch(showOverlay());

    try {
      await dispatch(
        deleteTeamMemberThunk({
          memberId: target.id,
          ownershipAssignments: assignments,
        })
      ).unwrap();

      await dispatch(fetchTeamMembersThunk({})).unwrap();

      setShowDeletePopup(false);
      setDeleteTarget(null);
      setTransferJobs([]);
      setReplacementMembers([]);
      setOwnershipAssignments({});
      setTransferLoadError("");

      setSuccessMessage(
        `${target.name} has been removed from your team successfully. Job ownership has been transferred and historical records are preserved.`
      );

      setShowSuccessPopup(true);
    } catch (error: any) {
      console.error("Failed to delete team member:", error);
      window.alert(getErrorMessage(error));
    } finally {
      setDeletingMember(false);
      dispatch(hideOverlay());
    }
  };
  /* =======================================================

     FILTERED MEMBERS

  ======================================================= */

  const filteredMembers =

    useMemo(() => {

      const query =

        searchQuery

          .trim()

          .toLowerCase();

      return (

        members as TeamMember[]

      ).filter((member) => {

        const matchesSearch =

          !query ||

          member.name

            ?.toLowerCase()

            .includes(query) ||

          member.email

            ?.toLowerCase()

            .includes(query) ||

          member.post

            ?.toLowerCase()

            .includes(query) ||

          member.department

            ?.toLowerCase()

            .includes(query) ||

          member.location

            ?.toLowerCase()

            .includes(query);

        const matchesRole =

          filterRole === "all" ||

          member.role ===

          filterRole;

        const matchesStatus =

          filterStatus === "all" ||

          member.status ===

          filterStatus;

        return (

          matchesSearch &&

          matchesRole &&

          matchesStatus

        );

      });

    }, [

      members,

      searchQuery,

      filterRole,

      filterStatus,

    ]);

  /* =======================================================

     STATS

  ======================================================= */

  const activeCount =

    useMemo(

      () =>

        (members as TeamMember[])

          .filter(

            (member) =>

              member.status ===

              "active"

          ).length,

      [members]

    );

  const onLeaveCount =

    useMemo(

      () =>

        (members as TeamMember[])

          .filter(

            (member) =>

              member.status ===

              "on_leave"

          ).length,

      [members]

    );

  const inactiveCount =

    useMemo(

      () =>

        (members as TeamMember[])

          .filter(

            (member) =>

              member.status ===

              "inactive"

          ).length,

      [members]

    );

  const stats = [

    {

      label: "Active",

      value: activeCount,

      description:

        "Currently available",

      icon: CheckCircle2,

      iconClass:

        "bg-emerald-50 text-emerald-600",

      valueClass:

        "text-emerald-700",

    },

    {

      label: "On Leave",

      value: onLeaveCount,

      description:

        "Temporarily unavailable",

      icon: Clock3,

      iconClass:

        "bg-amber-50 text-amber-600",

      valueClass:

        "text-amber-700",

    },

    {

      label: "Inactive",

      value: inactiveCount,

      description:

        "Currently inactive",

      icon: UserX,

      iconClass:

        "bg-red-50 text-red-600",

      valueClass:

        "text-red-700",

    },

  ];

  /* =======================================================

     RENDER

  ======================================================= */

  return (

    <div className="min-h-screen bg-slate-50">

      <main className="mx-auto w-full max-w-7xl px-3 py-4 sm:px-5 sm:py-6 lg:px-8 lg:py-8">

        <div className="space-y-5 sm:space-y-6">

          {/* Dashboard + Add Team Member */}

          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">

            <Link

              to="/hr"

              className="inline-flex w-fit items-center gap-2 text-sm font-medium text-slate-500 transition-colors hover:text-blue-600"

            >

              <ArrowLeft className="h-4 w-4" />

              Dashboard

            </Link>

            <Button

              onClick={handleCreate}

              disabled={saving}

              className="h-10 w-full bg-blue-600 px-4 font-semibold hover:bg-blue-700 sm:w-auto"

            >

              <Plus className="mr-2 h-4 w-4" />

              Add Team Member

            </Button>

          </div>


          {/* SH-21: Cross-HR reactivation transfer requests / notifications */}
          {(incomingTransferRequests.length > 0 || myTransferRequests.length > 0) && (
            <Card className="rounded-2xl border-slate-200 bg-white shadow-sm">
              <CardContent className="space-y-5 p-4 sm:p-5">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <h2 className="text-base font-semibold text-slate-900">
                      Reactivation Transfer Requests
                    </h2>
                    <p className="mt-1 text-sm text-slate-500">
                      Approve or reject requests to transfer a deleted Team Member into another HR team.
                    </p>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full sm:w-auto"
                    disabled={transferRequestsLoading}
                    onClick={() => void refreshTransferRequests()}
                  >
                    {transferRequestsLoading ? "Refreshing..." : "Refresh requests"}
                  </Button>
                </div>

                {incomingTransferRequests.length > 0 && (
                  <div className="space-y-3">
                    <h3 className="text-sm font-semibold text-slate-800">
                      Awaiting Your Approval ({incomingTransferRequests.length})
                    </h3>
                    {incomingTransferRequests.map((request) => (
                      <div
                        key={request.id}
                        className="flex flex-col gap-4 rounded-xl border border-amber-200 bg-amber-50/60 p-4 lg:flex-row lg:items-center lg:justify-between"
                      >
                        <div className="min-w-0 space-y-1">
                          <p className="font-semibold text-slate-900">
                            {request.memberName} <span className="font-normal text-slate-500">({request.memberEmail})</span>
                          </p>
                          <p className="text-sm text-slate-700">
                            <span className="font-medium">{request.requestedByName}</span> requested to transfer and reactivate this account under <span className="font-medium">{request.requestedHrAdminName}</span>.
                          </p>
                          <p className="text-xs text-slate-500">
                            Requested {request.createdAt ? new Date(request.createdAt).toLocaleString() : "recently"} · The account remains deleted until you approve.
                          </p>
                        </div>
                        <div className="flex shrink-0 flex-col gap-2 sm:flex-row">
                          <Button
                            type="button"
                            className="bg-emerald-600 hover:bg-emerald-700"
                            disabled={Boolean(processingTransferRequestId)}
                            onClick={() => void handleReactivationTransferDecision(request, "APPROVE")}
                          >
                            {processingTransferRequestId === request.id ? "Processing..." : "Approve & Reactivate"}
                          </Button>
                          <Button
                            type="button"
                            variant="outline"
                            className="border-red-200 text-red-700 hover:bg-red-50"
                            disabled={Boolean(processingTransferRequestId)}
                            onClick={() => void handleReactivationTransferDecision(request, "REJECT")}
                          >
                            Reject
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {myTransferRequests.length > 0 && (
                  <div className="space-y-3">
                    <h3 className="text-sm font-semibold text-slate-800">Request Status & Notifications</h3>
                    {myTransferRequests.slice(0, 5).map((request) => {
                      const statusStyles =
                        request.status === "APPROVED"
                          ? "border-emerald-200 bg-emerald-50 text-emerald-800"
                          : request.status === "REJECTED"
                            ? "border-red-200 bg-red-50 text-red-800"
                            : "border-amber-200 bg-amber-50 text-amber-800";
                      const previousHrAdminName =
                        request.currentHrAdminName || "the previous HR Admin";
                      const statusMessage =
                        request.status === "APPROVED"
                          ? `Approved — ${request.memberName} has been transferred to your team and reactivated.`
                          : request.status === "REJECTED"
                            ? `Rejected by ${previousHrAdminName} — the member remains deleted and ownership has not changed.`
                            : `This member is already associated with ${previousHrAdminName}. Your request has been sent to ${previousHrAdminName}. Once they approve it, this member will transfer to your team and reactivate automatically.`;

                      return (
                        <div
                          key={request.id}
                          className={`rounded-xl border p-3 ${statusStyles}`}
                        >
                          <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
                            <p className="text-sm font-semibold">
                              {request.memberName} ({request.memberEmail})
                            </p>
                            <Badge variant="outline" className="w-fit border-current bg-white/70">
                              {request.status}
                            </Badge>
                          </div>
                          <p className="mt-1 text-sm">{statusMessage}</p>
                          {request.reviewerNote && (
                            <p className="mt-1 text-xs opacity-80">
                              Reviewer note: {request.reviewerNote}
                            </p>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Stats */}
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3 sm:gap-4">


            {stats.map((stat) => {

              const Icon = stat.icon;

              return (

                <Card

                  key={stat.label}

                  className="rounded-2xl border-slate-200 bg-white shadow-sm transition-shadow hover:shadow-md"

                >

                  <CardContent className="p-4 sm:p-5">

                    <div className="flex items-center justify-between gap-4">

                      <div>

                        <p className="text-sm font-medium text-slate-500">

                          {stat.label}

                        </p>

                        <p

                          className={`mt-1 text-2xl font-bold sm:text-3xl ${stat.valueClass}`}

                        >

                          {stat.value}

                        </p>

                        <p className="mt-1 text-xs text-slate-400">

                          {stat.description}

                        </p>

                      </div>

                      <div

                        className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${stat.iconClass}`}

                      >

                        <Icon className="h-5 w-5" />

                      </div>

                    </div>

                  </CardContent>

                </Card>

              );

            })}

          </div>

          {/* Filters */}

          <Card className="rounded-2xl border-slate-200 bg-white shadow-sm">

            <CardContent className="p-3 sm:p-4">

              <div className="grid grid-cols-1 gap-3 lg:grid-cols-[minmax(0,1fr)_170px_170px]">

                <div className="relative min-w-0">

                  <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />

                  <Input

                    value={searchQuery}

                    onChange={(event) =>

                      setSearchQuery(

                        event.target.value

                      )

                    }

                    placeholder="Search by name, email, designation, department or location..."

                    className="h-11 bg-slate-50 pl-10 focus:bg-white"

                  />

                </div>

                <Select

                  value={filterRole}

                  onValueChange={

                    setFilterRole

                  }

                >

                  <SelectTrigger className="h-11 bg-slate-50">

                    <SelectValue placeholder="Role" />

                  </SelectTrigger>

                  <SelectContent>

                    <SelectItem value="all">

                      All Roles

                    </SelectItem>

                    {MANAGED_ROLES.map(

                      (role) => (

                        <SelectItem

                          key={role}

                          value={role}

                        >

                          {formatRole(role)}

                        </SelectItem>

                      )

                    )}

                  </SelectContent>

                </Select>

                <Select

                  value={filterStatus}

                  onValueChange={

                    setFilterStatus

                  }

                >

                  <SelectTrigger className="h-11 bg-slate-50">

                    <SelectValue placeholder="Status" />

                  </SelectTrigger>

                  <SelectContent>

                    <SelectItem value="all">

                      All Status

                    </SelectItem>

                    {STATUS_OPTIONS.map(

                      (status) => (

                        <SelectItem

                          key={status.value}

                          value={status.value}

                        >

                          {status.label}

                        </SelectItem>

                      )

                    )}

                  </SelectContent>

                </Select>

              </div>

            </CardContent>

          </Card>

          {/* Team Table */}

          <Card className="overflow-hidden rounded-2xl border-slate-200 bg-white shadow-sm">

            <CardContent className="p-0">

              <div className="overflow-x-auto">

                <Table className="min-w-[820px]">

                  <TableHeader>

                    <TableRow className="bg-slate-50 hover:bg-slate-50">

                      <TableHead className="h-12 px-5 font-semibold text-slate-600">

                        Member

                      </TableHead>

                      <TableHead className="h-12 px-4 font-semibold text-slate-600">

                        Role

                      </TableHead>

                      <TableHead className="h-12 px-4 font-semibold text-slate-600">

                        Designation

                      </TableHead>

                      <TableHead className="hidden h-12 px-4 font-semibold text-slate-600 md:table-cell">

                        Department

                      </TableHead>

                      <TableHead className="hidden h-12 px-4 font-semibold text-slate-600 sm:table-cell">

                        Location

                      </TableHead>

                      <TableHead className="h-12 px-4 font-semibold text-slate-600">

                        Status

                      </TableHead>

                      <TableHead className="h-12 px-5 text-right font-semibold text-slate-600">

                        Actions

                      </TableHead>

                    </TableRow>

                  </TableHeader>

                  <TableBody>

                    {loading ? (

                      <TableRow>

                        <TableCell

                          colSpan={7}

                          className="h-36 text-center text-sm text-slate-500"

                        >

                          Loading team members...

                        </TableCell>

                      </TableRow>

                    ) : filteredMembers.length === 0 ? (

                      <TableRow>

                        <TableCell

                          colSpan={7}

                          className="h-48 text-center"

                        >

                          <div className="mx-auto max-w-sm">

                            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-slate-100">

                              <Users className="h-5 w-5 text-slate-400" />

                            </div>

                            <p className="mt-3 text-sm font-semibold text-slate-700">

                              No team members found

                            </p>

                            <p className="mt-1 text-xs leading-5 text-slate-400">

                              Try changing your search or filters.

                            </p>

                          </div>

                        </TableCell>

                      </TableRow>

                    ) : (

                      filteredMembers.map(

                        (member) => (

                          <TableRow

                            key={member.id}

                            className="border-slate-100"

                          >

                            <TableCell className="px-5 py-4">

                              <div className="flex min-w-[220px] items-center gap-3">

                                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-blue-50 text-xs font-bold text-blue-600">

                                  {getInitials(

                                    member.name

                                  )}

                                </div>

                                <div className="min-w-0">

                                  <p className="truncate font-semibold text-slate-900">

                                    {member.name}

                                  </p>

                                  <p className="truncate text-xs text-slate-500">

                                    {member.email}

                                  </p>

                                </div>

                              </div>

                            </TableCell>

                            <TableCell className="px-4 py-4">

                              <Badge

                                variant="secondary"

                                className="border-0 bg-slate-100 text-xs font-semibold text-slate-700"

                              >

                                {formatRole(

                                  member.role

                                )}

                              </Badge>

                            </TableCell>

                            <TableCell className="max-w-[190px] px-4 py-4">

                              <span className="line-clamp-2 text-sm text-slate-700">

                                {member.post ||

                                  "-"}

                              </span>

                            </TableCell>

                            <TableCell className="hidden max-w-[180px] px-4 py-4 md:table-cell">

                              <span className="line-clamp-2 text-sm text-slate-700">

                                {member.department ||

                                  "-"}

                              </span>

                            </TableCell>

                            <TableCell className="hidden px-4 py-4 text-sm text-slate-700 sm:table-cell">

                              {member.location ||

                                "-"}

                            </TableCell>

                            <TableCell className="px-4 py-4">

                              <Badge

                                className={getStatusClasses(

                                  member.status

                                )}

                              >

                                {getStatusLabel(

                                  member.status

                                )}

                              </Badge>

                            </TableCell>

                            {/* ACTIONS */}

                            <TableCell className="px-5 py-4">

                              <div className="flex items-center justify-end gap-2 whitespace-nowrap">

                                {/* SH-22: MODULE PERMISSIONS */}
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  className="h-9 shrink-0 gap-2 rounded-lg border-blue-200 bg-blue-50 px-3 text-blue-700 shadow-none hover:border-blue-300 hover:bg-blue-100 hover:text-blue-800"
                                  title={`Grant or revoke module access for ${member.name}`}
                                  aria-label={`Manage access for ${member.name}`}
                                  disabled={saving}
                                  onClick={() => void handleManagePermissions(member)}
                                >
                                  <KeyRound className="h-4 w-4" />
                                  <span>Manage Access</span>
                                </Button>

                                {/* EDIT */}
                                <Button
                                  variant="ghost"
                                  size="icon"

                                  className="h-9 w-9 shrink-0 rounded-lg text-slate-500 hover:bg-blue-50 hover:text-blue-600"

                                  title="Edit member"

                                  disabled={saving}

                                  onClick={() =>

                                    handleEdit(

                                      member

                                    )

                                  }

                                >

                                  <Pencil className="h-4 w-4" />

                                </Button>

                                {/* STATUS DROPDOWN */}

                                <Select

                                  value={

                                    member.status?.toLowerCase() ||

                                    "inactive"

                                  }

                                  onValueChange={(

                                    status

                                  ) =>

                                    handleStatusChange(

                                      member,

                                      status

                                    )

                                  }

                                  disabled={saving}

                                >

                                  <SelectTrigger

                                    className="h-9 w-[118px] shrink-0 rounded-lg border-slate-200 bg-white px-3 text-xs font-medium text-slate-700 shadow-none focus:ring-0 focus:ring-offset-0"

                                  >

                                    <SelectValue />

                                  </SelectTrigger>

                                  <SelectContent>

                                    {STATUS_OPTIONS.map(

                                      (

                                        statusOption

                                      ) => (

                                        <SelectItem

                                          key={

                                            statusOption.value

                                          }

                                          value={

                                            statusOption.value

                                          }

                                        >

                                          {

                                            statusOption.label

                                          }

                                        </SelectItem>

                                      )

                                    )}

                                  </SelectContent>

                                </Select>

                                {/* DELETE */}

                                <Button

                                  variant="ghost"

                                  size="icon"

                                  className="h-9 w-9 shrink-0 rounded-lg text-red-500 hover:bg-red-50 hover:text-red-700"

                                  title="Remove member"

                                  disabled={saving}

                                  onClick={() =>

                                    handleDelete(

                                      member

                                    )

                                  }

                                >

                                  <Trash2 className="h-4 w-4" />

                                </Button>

                              </div>

                            </TableCell>

                          </TableRow>

                        )

                      )

                    )}

                  </TableBody>

                </Table>

              </div>

            </CardContent>

          </Card>

        </div>

      </main>

      {/* ===================================================

          ADD / EDIT DIALOG

      =================================================== */}

      <Dialog

        open={showDialog}

        onOpenChange={setShowDialog}

      >

        <DialogContent

          className="

            max-h-[92dvh]

            w-[calc(100vw-20px)]

            max-w-2xl

            overflow-y-auto

            rounded-2xl

            p-4

            sm:w-[calc(100vw-32px)]

            sm:p-6

          "

        >

          <DialogHeader className="text-left">

            <DialogTitle>

              {editingMember

                ? "Edit Team Member"

                : "Add Team Member"}

            </DialogTitle>

            <DialogDescription>

              {editingMember

                ? "Update the team member details."

                : "Create a new team member under your HR team."}

            </DialogDescription>

          </DialogHeader>

          <div className="space-y-5 py-2">

            {/* Name + Email */}

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">

              <div className="space-y-2">

                <Label htmlFor="hr-team-name">

                  Name *

                </Label>

                <Input

                  id="hr-team-name"

                  className="h-11"

                  value={memberName}

                  onChange={(event) =>

                    setMemberName(

                      event.target.value

                    )

                  }

                  placeholder="e.g. Amit Verma"

                />

              </div>

              <div className="space-y-2">

                <Label htmlFor="hr-team-email">

                  Email *

                </Label>

                <Input

                  id="hr-team-email"

                  type="email"

                  className="h-11"

                  value={memberEmail}

                  onChange={(event) =>

                    setMemberEmail(

                      event.target.value

                    )

                  }

                  placeholder="e.g. amit.verma@company.com"

                  disabled={Boolean(

                    editingMember

                  )}

                />

              </div>

            </div>

            {/* Role + Designation + Department */}

            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">

              <div className="space-y-2">

                <Label htmlFor="hr-team-role">

                  Role *

                </Label>

                <Select

                  value={memberRole}

                  onValueChange={

                    setMemberRole

                  }

                >

                  <SelectTrigger

                    id="hr-team-role"

                    className="h-11"

                  >

                    <SelectValue placeholder="Select role" />

                  </SelectTrigger>

                  <SelectContent>

                    {MANAGED_ROLES.map(

                      (role) => (

                        <SelectItem

                          key={role}

                          value={role}

                        >

                          {formatRole(role)}

                        </SelectItem>

                      )

                    )}

                  </SelectContent>

                </Select>

              </div>

              <div className="space-y-2">

                <Label htmlFor="hr-team-post">

                  Designation *

                </Label>

                <Input

                  id="hr-team-post"

                  className="h-11"

                  value={memberPost}

                  onChange={(event) =>

                    setMemberPost(

                      event.target.value

                    )

                  }

                  placeholder="e.g. Senior Recruiter"

                />

              </div>

              <div className="space-y-2">

                <Label htmlFor="hr-team-department">

                  Department *

                </Label>

                <Select

                  value={

                    memberDepartment

                  }

                  onValueChange={

                    setMemberDepartment

                  }

                >

                  <SelectTrigger

                    id="hr-team-department"

                    className="h-11"

                  >

                    <SelectValue placeholder="Select department" />

                  </SelectTrigger>

                  <SelectContent>

                    {DEPARTMENTS.map(

                      (department) => (

                        <SelectItem

                          key={department}

                          value={department}

                        >

                          {department}

                        </SelectItem>

                      )

                    )}

                  </SelectContent>

                </Select>

              </div>

            </div>

            {/* Location + Status */}

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">

              <div className="space-y-2">

                <Label htmlFor="hr-team-location">

                  Location

                </Label>

                <Input

                  id="hr-team-location"

                  className="h-11"

                  value={memberLocation}

                  onChange={(event) =>

                    setMemberLocation(

                      event.target.value

                    )

                  }

                  placeholder="e.g. Noida"

                />

              </div>

              <div className="space-y-2">

                <Label htmlFor="hr-team-status">

                  Status

                </Label>

                <Select

                  value={memberStatus}

                  onValueChange={

                    setMemberStatus

                  }

                >

                  <SelectTrigger

                    id="hr-team-status"

                    className="h-11"

                  >

                    <SelectValue placeholder="Select status" />

                  </SelectTrigger>

                  <SelectContent>

                    {STATUS_OPTIONS.map(

                      (status) => (

                        <SelectItem

                          key={

                            status.value

                          }

                          value={

                            status.value

                          }

                        >

                          {status.label}

                        </SelectItem>

                      )

                    )}

                  </SelectContent>

                </Select>

              </div>

            </div>

            {/* Password */}

            <div className="max-w-full space-y-2 md:max-w-[50%]">

              <Label htmlFor="hr-team-password">

                Password

                {!editingMember &&

                  " *"}

              </Label>

              <Input

                id="hr-team-password"

                type="password"

                className="h-11"

                value={

                  memberPassword

                }

                onChange={(event) =>

                  setMemberPassword(

                    event.target.value

                  )

                }

                autoComplete="new-password"

                placeholder={

                  editingMember

                    ? "Leave blank to keep current password"

                    : "Enter password"

                }

              />

            </div>

          </div>

          <DialogFooter className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">

            <Button

              variant="outline"

              className="h-11 w-full sm:w-auto"

              onClick={() => {

                setShowDialog(false);

                resetDialog();

              }}

            >

              Cancel

            </Button>

            <Button

              onClick={handleSave}

              className="h-11 w-full bg-blue-600 hover:bg-blue-700 sm:w-auto"

              disabled={saving}

            >

              <Save className="mr-2 h-4 w-4" />

              {editingMember

                ? "Update Member"

                : "Add Member"}

            </Button>

          </DialogFooter>

        </DialogContent>

      </Dialog>

      {/* ===================================================
           SH-22: MODULE PERMISSIONS DIALOG
       =================================================== */}

      <Dialog
        open={showPermissionsDialog}
        onOpenChange={(open) => {
          setShowPermissionsDialog(open);
          if (!open) {
            setPermissionTarget(null);
            setModulePermissions([]);
            setPermissionsError("");
            setPermissionsNotice("");
          }
        }}
      >
        <DialogContent className="max-h-[90dvh] w-[calc(100vw-20px)] max-w-xl overflow-y-auto rounded-2xl p-4 sm:w-[calc(100vw-32px)] sm:p-6">
          <DialogHeader className="text-left">
            <DialogTitle>Manage Module Permissions</DialogTitle>
            <DialogDescription>
              Choose which modules {permissionTarget?.name || "this team member"} can access.
              Changes are saved immediately.
            </DialogDescription>
          </DialogHeader>

          {permissionsError && (
            <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              {permissionsError}
            </div>
          )}

          {permissionsNotice && (
            <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
              {permissionsNotice}
            </div>
          )}

          {permissionsLoading ? (
            <div className="py-10 text-center text-sm text-slate-500">
              Loading module permissions...
            </div>
          ) : modulePermissions.length === 0 ? (
            !permissionsError && (
              <div className="py-10 text-center text-sm text-slate-500">
                No module permissions were returned.
              </div>
            )
          ) : (
            <div className="space-y-3 py-2">
              {modulePermissions.map((permission) => {
                const isSaving = permissionSavingKey === permission.featureKey;
                const isDashboard = permission.featureKey === "DASHBOARD";
                // Dashboard read access is a default permission for every team member.
                const isAllowed =
                  isDashboard || permission.permissionLevel === "ALLOWED";

                return (
                  <div
                    key={permission.featureKey}
                    className="flex flex-col gap-3 rounded-xl border border-slate-200 p-3 sm:flex-row sm:items-center sm:justify-between sm:p-4"
                  >
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="font-semibold text-slate-900">
                          {formatFeatureName(permission.featureKey)}
                        </p>
                        <Badge
                          variant="secondary"
                          className={
                            isDashboard
                              ? "border-0 bg-blue-50 text-blue-700"
                              : isAllowed
                                ? "border-0 bg-emerald-50 text-emerald-700"
                                : "border-0 bg-slate-100 text-slate-600"
                          }
                        >
                          {isDashboard
                            ? "Default access"
                            : isAllowed
                              ? "Allowed"
                              : "No access"}
                        </Badge>
                      </div>
                      <p className="mt-1 text-xs leading-5 text-slate-500">
                        {isDashboard
                          ? "Dashboard read access is granted by default to every team member and cannot be revoked."
                          : isAllowed
                            ? "This member can see and use this module."
                            : "This module will be hidden from this member."}
                      </p>
                    </div>

                    <div className="w-full shrink-0 sm:w-40">
                      <Select
                        value={isDashboard ? "ALLOWED" : permission.permissionLevel}
                        disabled={isDashboard || Boolean(permissionSavingKey)}
                        onValueChange={(value) => {
                          if (
                            !isDashboard &&
                            (value === "ALLOWED" || value === "NONE")
                          ) {
                            void handleFeaturePermissionChange(
                              permission.featureKey,
                              value
                            );
                          }
                        }}
                      >
                        <SelectTrigger className="h-10 bg-white">
                          <SelectValue placeholder="Set access" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="ALLOWED">Allowed</SelectItem>
                          <SelectItem value="NONE">No Access</SelectItem>
                        </SelectContent>
                      </Select>
                      {isSaving && (
                        <p className="mt-1 text-xs text-slate-500">
                          Saving permission...
                        </p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              className="w-full sm:w-auto"
              onClick={() => setShowPermissionsDialog(false)}
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ===================================================
    ACTIVE TEAM MEMBER TRANSFER APPROVAL
=================================================== */}

      <Dialog
        open={showTransferApprovalDialog}
        onOpenChange={(open) => {
          if (sendingActiveTransferRequest) return;

          setShowTransferApprovalDialog(open);

          if (!open) {
            setActiveTransferMember(null);
          }
        }}
      >
        <DialogContent className="w-[calc(100vw-24px)] max-w-lg rounded-2xl">
          <DialogHeader className="text-left">
            <DialogTitle>Transfer Approval Required</DialogTitle>

            <DialogDescription>
              This team member is already associated with another HR Admin.
              Send an approval request to transfer this member to your team.
            </DialogDescription>
          </DialogHeader>

          {activeTransferMember && (
            <div className="space-y-2 rounded-xl border border-slate-200 bg-slate-50 p-4">
              <p className="text-sm text-slate-600">
                <span className="font-medium text-slate-900">Name: </span>
                {activeTransferMember.name}
              </p>

              <p className="break-all text-sm text-slate-600">
                <span className="font-medium text-slate-900">Email: </span>
                {activeTransferMember.email}
              </p>
            </div>
          )}

          <DialogFooter className="flex flex-col-reverse gap-2 sm:flex-row">
            <Button
              type="button"
              variant="outline"
              disabled={sendingActiveTransferRequest}
              onClick={() => {
                setShowTransferApprovalDialog(false);
                setActiveTransferMember(null);
              }}
            >
              Cancel
            </Button>

            <Button
              type="button"
              disabled={
                sendingActiveTransferRequest ||
                !activeTransferMember?.memberId
              }
              onClick={() => void handleSendActiveTransferApproval()}
            >
              {sendingActiveTransferRequest
                ? "Sending..."
                : "Send Approval Request"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ===================================================

          REACTIVATE ACCOUNT

      =================================================== */}

      <AlreadyAppliedDialog

        open={showReactivateDialog}

        type="info"

        title="Previously Deleted Account"

        message="This email address was previously registered as a team member. Reactivate the existing account instead of creating a duplicate account."

        buttonText="Reactivate Account"

        primaryButtonDisabled={

          saving ||

          !reactivateName.trim() ||

          reactivatePassword.trim().length < 6

        }

        onOpenChange={(open) => {

          setShowReactivateDialog(open);

          if (!open) {

            resetReactivateDialog();

          }

        }}

        onViewApplicationStatus={

          handleReactivate

        }

        content={

          <div className="space-y-4">

            <div className="space-y-2">

              <Label htmlFor="reactivate-name">

                Name *

              </Label>

              <Input

                id="reactivate-name"

                className="h-11"

                value={reactivateName}

                onChange={(event) =>

                  setReactivateName(

                    event.target.value

                  )

                }

                placeholder="Enter team member name"

                autoComplete="name"

              />

            </div>

            <div className="space-y-2">

              <Label htmlFor="reactivate-email">

                Email

              </Label>

              <Input

                id="reactivate-email"

                type="email"

                className="h-11 bg-slate-50"

                value={reactivateEmail}

                readOnly

              />

              <p className="text-xs text-slate-400">

                The existing email cannot be changed during reactivation.

              </p>

            </div>

            <div className="space-y-2">

              <Label htmlFor="reactivate-password">

                New Password *

              </Label>

              <Input

                id="reactivate-password"

                type="password"

                className="h-11"

                value={

                  reactivatePassword

                }

                onChange={(event) =>

                  setReactivatePassword(

                    event.target.value

                  )

                }

                placeholder="Enter new password"

                autoComplete="new-password"

              />

              <p className="text-xs text-slate-400">

                Minimum 6 characters.

              </p>

            </div>

          </div>

        }

      />

      {/* ===================================================

          DELETE CONFIRMATION

      =================================================== */}

      <Dialog
        open={showDeletePopup}
        onOpenChange={(open) => {
          if (deletingMember) return;

          setShowDeletePopup(open);

          if (!open) {
            setDeleteTarget(null);
            setTransferJobs([]);
            setReplacementMembers([]);
            setOwnershipAssignments({});
            setTransferLoadError("");
          }
        }}
      >
        <DialogContent className="max-h-[90dvh] w-[calc(100vw-20px)] max-w-2xl overflow-y-auto rounded-2xl p-4 sm:p-6">
          <DialogHeader>
            <DialogTitle>Transfer Jobs & Delete Team Member</DialogTitle>

            <DialogDescription>
              {deleteTarget
                ? `Select a replacement owner for every job assigned to ${deleteTarget.name}. Existing job records and historical data will be preserved.`
                : "Transfer job ownership before deleting this team member."}
            </DialogDescription>
          </DialogHeader>

          {loadingTransferJobs ? (
            <div className="py-8 text-center text-sm text-slate-500">
              Loading owned jobs and active replacement members...
            </div>
          ) : transferLoadError ? (
            <div className="space-y-3">
              <div className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                {transferLoadError}
              </div>

              <Button
                type="button"
                variant="outline"
                disabled={deletingMember}
                onClick={() => {
                  if (deleteTarget) {
                    void handleDelete(deleteTarget);
                  }
                }}
              >
                Retry
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {transferJobs.length === 0 ? (
                <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-600">
                  No owned jobs were returned for this member. You can proceed
                  with deletion.
                </div>
              ) : (
                <>
                  <div className="text-sm font-semibold text-slate-800">
                    Jobs to transfer: {transferJobs.length}
                  </div>

                  {transferJobs.map((job) => (
                    <div
                      key={job.id}
                      className="space-y-3 rounded-xl border border-slate-200 p-3 sm:p-4"
                    >
                      <div>
                        <p className="font-semibold text-slate-900">
                          {job.title ||
                            job.jobTitle ||
                            job.positionTitle ||
                            `Job ${job.id}`}
                        </p>

                        {job.status && (
                          <p className="mt-1 text-xs text-slate-500">
                            Status: {job.status}
                          </p>
                        )}
                      </div>

                      <div className="space-y-2">
                        <Label>
                          Replacement owner <span className="text-red-500">*</span>
                        </Label>

                        <Select
                          value={ownershipAssignments[job.id] || ""}
                          onValueChange={(assignedToId) => {
                            setOwnershipAssignments((current) => ({
                              ...current,
                              [job.id]: assignedToId,
                            }));
                          }}
                          disabled={deletingMember}
                        >
                          <SelectTrigger className="h-11">
                            <SelectValue placeholder="Select active team member" />
                          </SelectTrigger>

                          <SelectContent>
                            {replacementMembers.map((candidate) => (
                              <SelectItem
                                key={candidate.id}
                                value={candidate.id}
                              >
                                {candidate.name}
                                {candidate.email
                                  ? ` (${candidate.email})`
                                  : ""}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  ))}
                </>
              )}

              {transferJobs.length > 0 &&
                replacementMembers.length === 0 && (
                  <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
                    No active replacement members are available. Deletion
                    cannot proceed until an eligible replacement is available.
                  </div>
                )}
            </div>
          )}

          <DialogFooter className="flex flex-col-reverse gap-2 sm:flex-row">
            <Button
              type="button"
              variant="outline"
              className="w-full sm:w-auto"
              disabled={deletingMember}
              onClick={() => {
                setShowDeletePopup(false);
                setDeleteTarget(null);
                setTransferJobs([]);
                setReplacementMembers([]);
                setOwnershipAssignments({});
                setTransferLoadError("");
              }}
            >
              Cancel
            </Button>

            <Button
              type="button"
              className="w-full bg-red-600 hover:bg-red-700 sm:w-auto"
              disabled={
                loadingTransferJobs ||
                deletingMember ||
                Boolean(transferLoadError) ||
                (transferJobs.length > 0 &&
                  (replacementMembers.length === 0 ||
                    transferJobs.some(
                      (job) => !ownershipAssignments[job.id]
                    )))
              }
              onClick={() => void handleConfirmDelete()}
            >
              <Trash2 className="mr-2 h-4 w-4" />
              {deletingMember ? "Transferring & Deleting..." : "Transfer & Delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ===================================================

          SUCCESS

      =================================================== */}

      <AlreadyAppliedDialog

        open={showSuccessPopup}

        type="deleteSuccess"

        title="Action Completed"

        message={successMessage}

        buttonText="Done"

        onOpenChange={

          setShowSuccessPopup

        }

        onViewApplicationStatus={() =>

          setShowSuccessPopup(

            false

          )

        }

      />

    </div>

  );

}
